import { __, sprintf } from '@wordpress/i18n';
import { useState, useEffect, useCallback } from '@wordpress/element';
import { Button, Spinner, Notice, TextControl } from '@wordpress/components';
import { ImageModal } from './';

const OrphanedFilesTab = ({ refreshStats }) => {
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [orphanedFiles, setOrphanedFiles] = useState([]);
    const [notice, setNotice] = useState(null);
    const [scanProgress, setScanProgress] = useState(null);
    const [actionLoading, setActionLoading] = useState({});
    const [selectedImage, setSelectedImage] = useState(null);
    
    // Search and Pagination state
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const itemsPerPage = 20;

    const fetchOrphanedFiles = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_get_orphaned_files',
                    nonce: mtrimAdminData.nonce,
                    search: search,
                    page: page,
                    limit: itemsPerPage,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setOrphanedFiles(data.data.items);
                setTotalPages(data.data.pages);
                setTotalItems(data.data.total);
            }
        } catch (error) {
            console.error('Error fetching orphaned files:', error);
        }
        setLoading(false);
    }, [search, page]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchOrphanedFiles();
        }, 300); // Debounce search
        return () => clearTimeout(timer);
    }, [fetchOrphanedFiles]);

    const startScan = async () => {
        setScanning(true);
        setScanProgress({ scanned: 0, total: 0, orphaned_found: 0 });

        let offset = 0;
        let isComplete = false;

        while (!isComplete) {
            try {
                const response = await fetch(mtrimAdminData.ajaxUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams({
                        action: 'mtrim_scan_filesystem',
                        nonce: mtrimAdminData.nonce,
                        offset: offset,
                    }),
                });
                const data = await response.json();
                
                if (data.success) {
                    setScanProgress({
                        scanned: data.data.next_offset,
                        total: data.data.total,
                        orphaned_found: (scanProgress?.orphaned_found || 0) + data.data.orphaned_found,
                    });
                    offset = data.data.next_offset;
                    isComplete = data.data.is_complete;
                } else {
                    isComplete = true;
                    setNotice({ type: 'error', message: __('Scan failed', 'media-trim') });
                }
            } catch (error) {
                isComplete = true;
                setNotice({ type: 'error', message: __('Scan error', 'media-trim') });
            }
        }

        setScanning(false);
        setPage(1); // Reset to first page
        fetchOrphanedFiles();
        setNotice({ type: 'success', message: __('Filesystem scan completed', 'media-trim') });
    };

    const handleDelete = async (fileId) => {
        setActionLoading({ ...actionLoading, [fileId]: 'delete' });
        try {
            const response = await fetch(mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_delete_orphaned_file',
                    nonce: mtrimAdminData.nonce,
                    file_id: fileId,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setOrphanedFiles(orphanedFiles.filter(f => f.id !== fileId));
                setNotice({ type: 'success', message: data.data.message });
                refreshStats();
                // If current page is empty and not on first page, go back a page
                if (orphanedFiles.length === 1 && page > 1) {
                    setPage(page - 1);
                } else {
                    fetchOrphanedFiles();
                }
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to delete file', 'media-trim') });
        }
        setActionLoading({ ...actionLoading, [fileId]: null });
    };

    const handleAddToLibrary = async (fileId) => {
        setActionLoading({ ...actionLoading, [fileId]: 'add' });
        try {
            const response = await fetch(mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_add_to_media_library',
                    nonce: mtrimAdminData.nonce,
                    file_id: fileId,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setOrphanedFiles(orphanedFiles.filter(f => f.id !== fileId));
                setNotice({ type: 'success', message: data.data.message });
                refreshStats();
                if (orphanedFiles.length === 1 && page > 1) {
                    setPage(page - 1);
                } else {
                    fetchOrphanedFiles();
                }
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to add to media library', 'media-trim') });
        }
        setActionLoading({ ...actionLoading, [fileId]: null });
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        return (
            <div className="mtrim-pagination">
                <Button 
                    variant="secondary" 
                    disabled={page === 1} 
                    onClick={() => setPage(page - 1)}
                >
                    {__('Previous', 'media-trim')}
                </Button>
                <span className="mtrim-page-info">
                    {sprintf(__('Page %1$s of %2$s', 'media-trim'), page, totalPages)}
                </span>
                <Button 
                    variant="secondary" 
                    disabled={page === totalPages} 
                    onClick={() => setPage(page + 1)}
                >
                    {__('Next', 'media-trim')}
                </Button>
            </div>
        );
    };

    return (
        <div className="mtrim-orphaned-files-tab">
            <div className="mtrim-tab-header">
                <h2>{__('Orphaned Files', 'media-trim')}</h2>
                <p className="mtrim-description">
                    {__('Files found in uploads folder that are not registered in the WordPress Media Library.', 'media-trim')}
                </p>
            </div>

            {notice && (
                <Notice 
                    status={notice.type} 
                    isDismissible={true}
                    onRemove={() => setNotice(null)}
                >
                    {notice.message}
                </Notice>
            )}

            <div className="mtrim-orphaned-stats">
                <div className="mtrim-stat-card">
                    <span className="dashicons dashicons-media-document"></span>
                    <div className="mtrim-stat-info">
                        <span className="mtrim-stat-value">{totalItems}</span>
                        <span className="mtrim-stat-label">{__('Total Orphaned', 'media-trim')}</span>
                    </div>
                </div>
                
                <div className="mtrim-orphaned-search">
                    <TextControl
                        placeholder={__('Search files...', 'media-trim')}
                        value={search}
                        onChange={(val) => {
                            setSearch(val);
                            setPage(1);
                        }}
                    />
                </div>

                <Button
                    variant="primary"
                    onClick={startScan}
                    disabled={scanning}
                >
                    {scanning ? (
                        <>
                            <Spinner />
                            {__('Scanning...', 'media-trim')}
                        </>
                    ) : (
                        <>
                            <span className="dashicons dashicons-search"></span>
                            {__('Scan Filesystem', 'media-trim')}
                        </>
                    )}
                </Button>
            </div>

            {scanning && scanProgress && (
                <div className="mtrim-scan-progress">
                    <div className="mtrim-progress-bar">
                        <div 
                            className="mtrim-progress-fill" 
                            style={{ width: `${(scanProgress.scanned / (scanProgress.total || 1)) * 100}%` }}
                        ></div>
                    </div>
                    <p>
                        {__('Scanned:', 'media-trim')} {scanProgress.scanned} / {scanProgress.total}
                    </p>
                </div>
            )}

            {loading && !scanning ? (
                <div className="mtrim-loading-container">
                    <Spinner />
                    <p>{__('Loading orphaned files...', 'media-trim')}</p>
                </div>
            ) : orphanedFiles.length === 0 ? (
                <div className="mtrim-empty-state">
                    <span className="dashicons dashicons-yes-alt"></span>
                    <h3>{__('No Orphaned Files Found', 'media-trim')}</h3>
                    <p>{__('Try scanning the filesystem or change your search query.', 'media-trim')}</p>
                </div>
            ) : (
                <>
                    <div className="mtrim-orphaned-grid">
                        {orphanedFiles.map((file) => (
                            <div key={file.id} className="mtrim-orphaned-item">
                                <div 
                                    className="mtrim-orphaned-item-preview"
                                    onClick={() => setSelectedImage(file.file_url)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {file.is_image ? (
                                        <img src={file.file_url} alt={file.file_name} />
                                    ) : (
                                        <span className="dashicons dashicons-media-default"></span>
                                    )}
                                </div>
                                <div className="mtrim-orphaned-item-info">
                                    <h4 title={file.file_name}>{file.file_name}</h4>
                                    <div className="mtrim-orphaned-item-meta">
                                        <span>{file.formatted_size}</span>
                                        <span className="mtrim-separator">•</span>
                                        <span title={file.relative_path}>{file.relative_path.substring(0, 25)}...</span>
                                    </div>
                                </div>
                                <div className="mtrim-orphaned-item-actions">
                                    <Button
                                        variant="secondary"
                                        onClick={() => handleAddToLibrary(file.id)}
                                        disabled={actionLoading[file.id]}
                                    >
                                        {actionLoading[file.id] === 'add' ? <Spinner /> : __('Add to Library', 'media-trim')}
                                    </Button>
                                    <Button
                                        isDestructive
                                        onClick={() => handleDelete(file.id)}
                                        disabled={actionLoading[file.id]}
                                    >
                                        {actionLoading[file.id] === 'delete' ? <Spinner /> : __('Delete', 'media-trim')}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {renderPagination()}
                </>
            )}

            {selectedImage && (
                <ImageModal 
                    imageUrl={selectedImage} 
                    onClose={() => setSelectedImage(null)} 
                />
            )}

            <style>{`
                .mtrim-orphaned-search {
                    flex: 1;
                    max-width: 300px;
                }
                .mtrim-orphaned-search .components-base-control__field {
                    margin-bottom: 0;
                }
                .mtrim-orphaned-search input {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    padding: 10px 14px !important;
                }
                .mtrim-pagination {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 20px;
                    margin-top: 40px;
                    padding-top: 20px;
                    border-top: 1px solid #f1f5f9;
                }
                .mtrim-page-info {
                    font-weight: 600;
                    color: #475569;
                }
                .mtrim-orphaned-item-preview img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    border-radius: 12px;
                }
            `}</style>
        </div>
    );
};

export default OrphanedFilesTab;
